import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "../ModalBase";
import LoadingSpinner from "../LoadingSpinner";
import { hasValidCoords, loadGoogleMaps } from "../../helpers/googleMaps";
import { getStatusHexColor, tailwindColors } from "../../theme/gitoColors";
import { useAuth, Roles } from "../../providers/AuthContext";

const DEFAULT_CENTER = { lat: 62.1983, lng: 17.5672 };
const DEFAULT_ZOOM = 5;
const SITE_ZOOM = 15;

const BUCKET_COLORS = {
    available: getStatusHexColor("AVAILABLE"),
    occupied: getStatusHexColor("OCCUPIED"),
    unavailable: getStatusHexColor("UNAVAILABLE"),
};

const OCCUPIED_STATUSES = [
    "OCCUPIED",
    "PREPARING",
    "CHARGING",
    "EVCONNECTED",
    "IDLE",
    "FINISHING",
    "RESERVED",
    "SUSPENDEDEV",
    "SUSPENDEDEVSE",
    "PENDING",
];

const UNAVAILABLE_STATUSES = ["UNAVAILABLE", "INOPERATIVE", "FAULTED"];

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const classifyConnector = (item) => {
    const data = item?.Data || {};
    const status = (data.Status || data.chargingConnectorStatus || "").toUpperCase();
    if (!data.isOnline) {
        return "unavailable";
    }
    if (status === "AVAILABLE") {
        return "available";
    }
    if (UNAVAILABLE_STATUSES.includes(status)) {
        return "unavailable";
    }
    if (OCCUPIED_STATUSES.includes(status)) {
        return "occupied";
    }
    return "unavailable";
};

const PIN_COLUMNS = [
    { key: "available", color: BUCKET_COLORS.available },
    { key: "occupied", color: BUCKET_COLORS.occupied },
    { key: "unavailable", color: BUCKET_COLORS.unavailable },
];

const PIN_WIDTH = 64;
const PIN_HEIGHT = 44;
const PIN_COLLISION_PADDING = 6;

const pinRect = (x, y) => ({
    left: x - PIN_WIDTH / 2 - PIN_COLLISION_PADDING,
    top: y - PIN_HEIGHT - PIN_COLLISION_PADDING,
    right: x + PIN_WIDTH / 2 + PIN_COLLISION_PADDING,
    bottom: y + PIN_COLLISION_PADDING,
});

const rectsOverlap = (a, b) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

const sumBuckets = (sites) =>
    sites.reduce(
        (acc, site) => ({
            available: acc.available + site.available,
            occupied: acc.occupied + site.occupied,
            unavailable: acc.unavailable + site.unavailable,
        }),
        { available: 0, occupied: 0, unavailable: 0 }
    );

const toPixel = (map, lat, lng) => {
    const projection = map.getProjection();
    if (!projection) {
        return null;
    }
    const point = projection.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
    const scale = 2 ** map.getZoom();
    return { x: point.x * scale, y: point.y * scale };
};

const clusterOverlappingSites = (sites, map) => {
    if (!sites.length) {
        return [];
    }

    const items = sites.map((site) => {
        const lat = Number(site.latitude);
        const lng = Number(site.longitude);
        const pixel = map ? toPixel(map, lat, lng) : null;
        return {
            site,
            lat,
            lng,
            rect: pixel ? pinRect(pixel.x, pixel.y) : null,
        };
    });

    const parent = items.map((_, index) => index);
    const find = (index) => {
        if (parent[index] !== index) {
            parent[index] = find(parent[index]);
        }
        return parent[index];
    };
    const union = (a, b) => {
        const rootA = find(a);
        const rootB = find(b);
        if (rootA !== rootB) {
            parent[rootA] = rootB;
        }
    };

    if (items[0].rect) {
        for (let i = 0; i < items.length; i += 1) {
            for (let j = i + 1; j < items.length; j += 1) {
                if (rectsOverlap(items[i].rect, items[j].rect)) {
                    union(i, j);
                }
            }
        }
    }

    const groups = new Map();
    items.forEach((item, index) => {
        const root = find(index);
        if (!groups.has(root)) {
            groups.set(root, []);
        }
        groups.get(root).push(item);
    });

    return Array.from(groups.values()).map((group) => {
        const members = group.map((item) => item.site).sort((a, b) => a.name.localeCompare(b.name));
        const buckets = sumBuckets(members);
        return {
            id: members.map((site) => site.id).join("|"),
            sites: members,
            latitude: group.reduce((sum, item) => sum + item.lat, 0) / group.length,
            longitude: group.reduce((sum, item) => sum + item.lng, 0) / group.length,
            ...buckets,
        };
    });
};

const clusterHasSpread = (cluster) => {
    if (cluster.sites.length < 2) {
        return false;
    }
    const lats = cluster.sites.map((site) => Number(site.latitude));
    const lngs = cluster.sites.map((site) => Number(site.longitude));
    return Math.max(...lats) - Math.min(...lats) > 0.00015 || Math.max(...lngs) - Math.min(...lngs) > 0.00015;
};

const INFO_WINDOW_STYLE_ID = "gito-site-map-infowindow-style";
const TOOLTIP_PRIMARY = tailwindColors.primary[500];
const TOOLTIP_MUTE_200 = tailwindColors.mute[200];
const TOOLTIP_MUTE_500 = tailwindColors.mute[500];
const TOOLTIP_MUTE_600 = tailwindColors.mute[600];

const formatConnectorKw = (value) => {
    const wattsOrKw = Number(value);
    if (!Number.isFinite(wattsOrKw) || wattsOrKw <= 0) {
        return "";
    }
    const kw = wattsOrKw >= 1000 ? wattsOrKw / 1000 : wattsOrKw;
    const rounded = Number.isInteger(kw) ? kw : Math.round(kw * 10) / 10;
    return `${rounded} kW`;
};

const ensureSiteMapInfoWindowStyles = () => {
    let style = document.getElementById(INFO_WINDOW_STYLE_ID);
    if (!style) {
        style = document.createElement("style");
        style.id = INFO_WINDOW_STYLE_ID;
        document.head.appendChild(style);
    }
    style.textContent = `
        .gm-style-iw-c:has(.gito-map-tooltip) {
            padding: 0 !important;
            max-width: 280px !important;
            border-radius: 12px !important;
            border: 1px solid ${TOOLTIP_MUTE_200} !important;
            box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
            background: #ffffff !important;
            overflow: hidden !important;
        }
        .gm-style-iw-d:has(.gito-map-tooltip) {
            overflow: auto !important;
            max-height: 380px !important;
            padding: 0 !important;
        }
        .gm-style-iw-c:has(.gito-map-tooltip) .gm-style-iw-ch,
        .gm-style-iw-c:has(.gito-map-tooltip) .gm-style-iw-chr,
        .gm-style-iw-c:has(.gito-map-tooltip) button.gm-ui-hover-effect {
            display: none !important;
        }
        .gm-style-iw-t:has(.gito-map-tooltip) .gm-style-iw-tc::after {
            background: #ffffff;
        }
        .gito-map-tooltip {
            min-width: 200px;
            max-width: 260px;
            padding: 8px 12px 8px;
            font-family: Inter, "Segoe UI", system-ui, sans-serif;
            font-size: 12px;
            font-weight: 400;
            color: ${TOOLTIP_PRIMARY};
            text-align: left;
        }
        .gito-map-tooltip__header {
            padding-bottom: 6px;
            border-bottom: 1px solid ${TOOLTIP_MUTE_200};
        }
        .gito-map-tooltip__title {
            font-size: 13px;
            font-weight: 700;
            line-height: 1.3;
            color: ${TOOLTIP_PRIMARY};
        }
        .gito-map-tooltip__meta {
            margin-top: 2px;
            font-size: 11px;
            color: ${TOOLTIP_MUTE_500};
        }
        .gito-map-tooltip__stats {
            display: flex;
            flex-wrap: wrap;
            gap: 8px 10px;
            margin: 6px 0;
        }
        .gito-map-tooltip__stat {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 11px;
            color: ${TOOLTIP_MUTE_600};
        }
        .gito-map-tooltip__stat-dot {
            width: 7px;
            height: 7px;
            border-radius: 99px;
            display: inline-block;
        }
        .gito-map-tooltip__stat b {
            font-size: 12px;
            font-weight: 700;
            font-variant-numeric: tabular-nums;
        }
        .gito-map-tooltip__list {
            display: flex;
            flex-direction: column;
            gap: 2px;
            padding-top: 4px;
            border-top: 1px solid ${TOOLTIP_MUTE_200};
        }
        .gito-map-tooltip__row {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 6px;
            align-items: center;
            min-height: 18px;
        }
        .gito-map-tooltip__dot {
            width: 7px;
            height: 7px;
            border-radius: 99px;
            display: inline-block;
        }
        .gito-map-tooltip__row-main {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            min-width: 0;
        }
        .gito-map-tooltip__name {
            font-size: 11px;
            font-weight: 600;
            color: ${TOOLTIP_PRIMARY};
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .gito-map-tooltip__link {
            color: ${TOOLTIP_PRIMARY};
            text-decoration: none;
        }
        .gito-map-tooltip__link:hover {
            text-decoration: underline;
        }
        .gito-map-tooltip__kw {
            font-size: 11px;
            font-weight: 600;
            color: ${TOOLTIP_PRIMARY};
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
            flex-shrink: 0;
        }
    `;
};

const buildCountChipsHtml = (counts, t) =>
    PIN_COLUMNS.map((column) => {
        const value = counts[column.key] || 0;
        const label = t(`chargeMonitor.mapBucket.${column.key}`);
        return `<span class="gito-map-tooltip__stat">
            <span class="gito-map-tooltip__stat-dot" style="background:${column.color};"></span>
            <b style="color:${column.color};">${value}</b>
            ${escapeHtml(label)}
        </span>`;
    }).join("");

const buildSiteDetailsLink = (site, className = "") => {
    if (!site.publicId) {
        return className
            ? `<span class="${className}">${escapeHtml(site.name)}</span>`
            : escapeHtml(site.name);
    }

    const classes = [className, "gito-map-tooltip__link"].filter(Boolean).join(" ");
    return `<a class="${classes}" href="/site/${encodeURIComponent(site.publicId)}" target="_blank" rel="noopener noreferrer">${escapeHtml(site.name)}</a>`;
};

const buildSiteTooltipHtml = (site, canOpenPointEvents, t) => {
    const displayName = site.name === "Unknown site" ? t("chargeMonitor.unknownSite") : site.name;
    const displaySite = { ...site, name: displayName };
    const connectorRows = site.connectors
        .map((connector) => {
            const color = BUCKET_COLORS[connector.bucket];
            const powerLabel = formatConnectorKw(connector.maxElectricPower);
            const connectorName = connector.name === "Connector" ? t("entity.connector") : connector.name;
            const nameHtml =
                canOpenPointEvents && connector.pointPublicId
                    ? `<a class="gito-map-tooltip__name gito-map-tooltip__link" href="/point/${encodeURIComponent(connector.pointPublicId)}?tab=events" target="_blank" rel="noopener noreferrer">${escapeHtml(connectorName)}</a>`
                    : `<span class="gito-map-tooltip__name">${escapeHtml(connectorName)}</span>`;
            return `<div class="gito-map-tooltip__row">
                <span class="gito-map-tooltip__dot" style="background:${color};"></span>
                <div class="gito-map-tooltip__row-main">
                    ${nameHtml}
                    ${powerLabel ? `<span class="gito-map-tooltip__kw">${escapeHtml(powerLabel)}</span>` : ""}
                </div>
            </div>`;
        })
        .join("");

    return `
        <div class="gito-map-tooltip">
            <div class="gito-map-tooltip__header">
                <div class="gito-map-tooltip__title">${buildSiteDetailsLink(displaySite)}</div>
            </div>
            <div class="gito-map-tooltip__stats">${buildCountChipsHtml(site, t)}</div>
            <div class="gito-map-tooltip__list">${connectorRows}</div>
        </div>
    `;
};

const buildClusterTooltipHtml = (cluster, t) => {
    const siteRows = cluster.sites
        .map((site) => {
            const chips = buildCountChipsHtml(site, t);
            const displaySite = {
                ...site,
                name: site.name === "Unknown site" ? t("chargeMonitor.unknownSite") : site.name,
            };
            return `<div>
                ${buildSiteDetailsLink(displaySite, "gito-map-tooltip__title")}
                <div class="gito-map-tooltip__stats" style="margin:4px 0 8px;">${chips}</div>
            </div>`;
        })
        .join("");

    return `
        <div class="gito-map-tooltip">
            <div class="gito-map-tooltip__stats">${buildCountChipsHtml(cluster, t)}</div>
            <div class="gito-map-tooltip__list">${siteRows}</div>
        </div>
    `;
};

const buildCountDot = (cx, cy, value, color) => {
    const fontSize = String(value).length > 2 ? 9 : 11;
    return `
        <circle cx="${cx}" cy="${cy}" r="9.5" fill="${color}" stroke="#002F6C" stroke-width="0.9" />
        <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="800" fill="#ffffff">${value}</text>
    `;
};

const buildPieMarkerIcon = ({ available, occupied, unavailable, siteCount = 1 }) => {
    const counts = { available, occupied, unavailable };
    const dots = PIN_COLUMNS.map((column, index) =>
        buildCountDot(14 + index * 18, 14.5, counts[column.key], column.color)
    ).join("");
    const countLabel = siteCount > 9 ? "9+" : String(siteCount);
    const badge =
        siteCount > 1
            ? `
                <circle cx="58" cy="7" r="7" fill="#002F6C" stroke="#ffffff" stroke-width="1.25" />
                <text x="58" y="7" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="9" font-weight="800" fill="#ffffff">${countLabel}</text>
            `
            : "";

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 ${PIN_WIDTH} ${PIN_HEIGHT}">
            <ellipse cx="32" cy="42" rx="8" ry="1.75" fill="#002F6C" opacity="0.18" />
            <path d="M8 1 H56 A7 7 0 0 1 63 8 V21 A7 7 0 0 1 56 28 H37 L32 43 L27 28 H8 A7 7 0 0 1 1 21 V8 A7 7 0 0 1 8 1 Z" fill="#ffffff" stroke="#002F6C" stroke-width="1.35" stroke-linejoin="round" />
            ${dots}
            ${badge}
        </svg>
    `.trim();

    return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: PIN_WIDTH, height: PIN_HEIGHT },
        anchor: { x: PIN_WIDTH / 2, y: PIN_HEIGHT - 1 },
    };
};

const toGoogleMarkerIcon = (iconSpec) => ({
    url: iconSpec.url,
    scaledSize: new window.google.maps.Size(iconSpec.scaledSize.width, iconSpec.scaledSize.height),
    anchor: new window.google.maps.Point(iconSpec.anchor.x, iconSpec.anchor.y),
});

const syncClusterFromLatestSites = (cluster, sitesById) => {
    const latestSites = cluster.sites
        .map((site) => sitesById.get(site.id))
        .filter(Boolean);
    if (!latestSites.length) {
        return;
    }
    const buckets = sumBuckets(latestSites);
    cluster.sites = latestSites;
    cluster.available = buckets.available;
    cluster.occupied = buckets.occupied;
    cluster.unavailable = buckets.unavailable;
};

export const buildSiteSummaries = (statusUpdates = []) => {
    const bySite = new Map();

    statusUpdates.forEach((item) => {
        const data = item?.Data || {};
        const sitePublicId = data.chargingSitePublicId || data.ChargingSitePublicId || "";
        const siteName = data.ChargingSiteName || data.chargingSiteName || "Unknown site";
        const key = sitePublicId || siteName;
        if (!key) {
            return;
        }

        if (!bySite.has(key)) {
            const latitude = data.latitude ?? data.Latitude;
            const longitude = data.longitude ?? data.Longitude;
            bySite.set(key, {
                id: key,
                publicId: sitePublicId,
                name: siteName,
                latitude: hasValidCoords(latitude, longitude) ? latitude : null,
                longitude: hasValidCoords(latitude, longitude) ? longitude : null,
                available: 0,
                occupied: 0,
                unavailable: 0,
                connectors: [],
            });
        }

        const site = bySite.get(key);
        if (!hasValidCoords(site.latitude, site.longitude) && hasValidCoords(data.latitude ?? data.Latitude, data.longitude ?? data.Longitude)) {
            site.latitude = data.latitude ?? data.Latitude;
            site.longitude = data.longitude ?? data.Longitude;
        }

        const bucket = classifyConnector(item);
        site[bucket] += 1;
        site.connectors.push({
            publicId: data.chargingConnectorPublicId || data.ChargingConnectorPublicId || "",
            pointPublicId: data.chargingPointPublicId || data.ChargingPointPublicId || "",
            name: data.chargingConnectorName || data.ChargingConnectorName || "Connector",
            pointName: data.chargingPointName || data.ChargingPointName || "",
            status: (data.Status || data.chargingConnectorStatus || "").toUpperCase(),
            isOnline: !!data.isOnline,
            maxElectricPower: data.maxElectricPower ?? data.MaxElectricPower,
            bucket,
        });
    });

    return Array.from(bySite.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const siteMatchesSearch = (site, query) => {
    const connectorMatch = site.connectors.some((connector) =>
        `${connector.name} ${connector.pointName}`.toLowerCase().includes(query)
    );
    return site.name.toLowerCase().includes(query) || connectorMatch;
};

const SiteOperationsMapModal = ({ isOpen, onClose, statusUpdates = [] }) => {
    const { t } = useTranslation();
    const tRef = useRef(t);
    tRef.current = t;
    const { hasAnyRole } = useAuth();
    const canOpenPointEventsRef = useRef(false);
    canOpenPointEventsRef.current = hasAnyRole([Roles.Admin, Roles.CpmsLight]);
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef(new Map());
    const infoWindowRef = useRef(null);
    const sitesByIdRef = useRef(new Map());
    const clustersByIdRef = useRef(new Map());
    const pendingFocusIdRef = useRef(null);
    const shouldFitRef = useRef(true);
    const openSiteInfoRef = useRef(null);
    const openClusterInfoRef = useRef(null);
    const showPinTooltipRef = useRef(null);
    const scheduleHoverCloseRef = useRef(null);
    const cancelHoverCloseRef = useRef(null);
    const openTooltipRef = useRef(null);
    const tooltipContentElRef = useRef(null);
    const tooltipStickyRef = useRef(false);
    const hoverCloseTimeoutRef = useRef(0);
    const refreshOpenTooltipRef = useRef(null);
    const filteredSitesRef = useRef([]);
    const [isLoading, setIsLoading] = useState(true);
    const [mapReady, setMapReady] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSiteId, setSelectedSiteId] = useState(null);

    const sites = useMemo(() => buildSiteSummaries(statusUpdates), [statusUpdates]);
    const mappedSites = useMemo(
        () => sites.filter((site) => hasValidCoords(site.latitude, site.longitude)),
        [sites]
    );
    const unmappedSites = useMemo(
        () => sites.filter((site) => !hasValidCoords(site.latitude, site.longitude)),
        [sites]
    );
    const filteredSites = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return mappedSites;
        }
        return mappedSites.filter((site) => siteMatchesSearch(site, query));
    }, [mappedSites, searchQuery]);
    const filteredUnmappedSites = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return unmappedSites;
        }
        return unmappedSites.filter((site) => siteMatchesSearch(site, query));
    }, [unmappedSites, searchQuery]);

    filteredSitesRef.current = filteredSites;

    const statusTotals = useMemo(() => sumBuckets(filteredSites), [filteredSites]);

    const siteLayoutKey = useMemo(
        () =>
            mappedSites
                .map((site) => `${site.id}:${Number(site.latitude)}:${Number(site.longitude)}`)
                .join("|"),
        [mappedSites]
    );

    useEffect(() => {
        shouldFitRef.current = true;
    }, [searchQuery, isOpen]);

    sitesByIdRef.current = new Map(filteredSites.map((site) => [site.id, site]));

    const getTooltipContentEl = () => {
        if (!tooltipContentElRef.current) {
            tooltipContentElRef.current = document.createElement("div");
            tooltipContentElRef.current.addEventListener("mouseenter", () => {
                cancelHoverCloseRef.current?.();
            });
            tooltipContentElRef.current.addEventListener("mouseleave", () => {
                scheduleHoverCloseRef.current?.();
            });
        }
        return tooltipContentElRef.current;
    };

    const setTooltipHtml = (html) => {
        const el = getTooltipContentEl();
        el.innerHTML = html;
        return el;
    };

    const ensureInfoWindow = () => {
        if (infoWindowRef.current || !window.google?.maps) {
            return infoWindowRef.current;
        }

        const infoWindow = new window.google.maps.InfoWindow({
            maxWidth: 280,
            headerDisabled: true,
            pixelOffset: new window.google.maps.Size(0, -8),
        });
        infoWindow.addListener("closeclick", () => {
            openTooltipRef.current = null;
            tooltipStickyRef.current = false;
        });
        infoWindow.addListener("domready", () => {
            const tooltip = document.querySelector(".gito-map-tooltip");
            const frame = tooltip?.closest(".gm-style-iw-a") || tooltip?.closest(".gm-style-iw-t") || tooltip?.closest(".gm-style-iw");
            if (!frame || frame.dataset.gitoHoverBound === "true") {
                return;
            }
            frame.dataset.gitoHoverBound = "true";
            frame.addEventListener("mouseenter", () => {
                cancelHoverCloseRef.current?.();
            });
            frame.addEventListener("mouseleave", () => {
                scheduleHoverCloseRef.current?.();
            });
        });
        infoWindowRef.current = infoWindow;
        return infoWindow;
    };

    const cancelHoverClose = () => {
        window.clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = 0;
    };

    const scheduleHoverClose = () => {
        if (tooltipStickyRef.current) {
            return;
        }
        cancelHoverClose();
        hoverCloseTimeoutRef.current = window.setTimeout(() => {
            if (tooltipStickyRef.current) {
                return;
            }
            closeTooltip();
        }, 80);
    };

    const closeTooltip = () => {
        cancelHoverClose();
        tooltipStickyRef.current = false;
        openTooltipRef.current = null;
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    };

    const refreshOpenTooltip = () => {
        const open = openTooltipRef.current;
        const info = infoWindowRef.current;
        if (!open || !info || !info.getMap()) {
            if (open && info && !info.getMap()) {
                openTooltipRef.current = null;
            }
            return;
        }

        if (open.kind === "site") {
            const site = sitesByIdRef.current.get(open.id);
            if (!site) {
                closeTooltip();
                return;
            }
            info.setContent(setTooltipHtml(buildSiteTooltipHtml(site, canOpenPointEventsRef.current, tRef.current)));
            return;
        }

        const cluster = clustersByIdRef.current.get(open.id);
        if (!cluster) {
            closeTooltip();
            return;
        }
        if (cluster.sites.length === 1) {
            info.setContent(setTooltipHtml(buildSiteTooltipHtml(cluster.sites[0], canOpenPointEventsRef.current, tRef.current)));
            return;
        }
        info.setContent(setTooltipHtml(buildClusterTooltipHtml(cluster, tRef.current)));
    };

    const openSiteInfo = (site, marker, sticky = false) => {
        if (!mapRef.current || !window.google?.maps || !site) {
            return;
        }

        cancelHoverClose();
        tooltipStickyRef.current = sticky;
        ensureSiteMapInfoWindowStyles();
        const infoWindow = ensureInfoWindow();
        if (!infoWindow) {
            return;
        }

        openTooltipRef.current = { kind: "site", id: site.id };
        infoWindow.setContent(setTooltipHtml(buildSiteTooltipHtml(site, canOpenPointEventsRef.current, tRef.current)));
        infoWindow.open(mapRef.current, marker);
        setSelectedSiteId(site.id);
    };

    const showPinTooltip = (cluster, marker, sticky = false) => {
        if (!cluster || !marker || !mapRef.current) {
            return;
        }
        if (cluster.sites.length === 1) {
            openSiteInfo(cluster.sites[0], marker, sticky);
            return;
        }

        cancelHoverClose();
        tooltipStickyRef.current = sticky;
        ensureSiteMapInfoWindowStyles();
        const infoWindow = ensureInfoWindow();
        if (!infoWindow) {
            return;
        }

        openTooltipRef.current = { kind: "cluster", id: cluster.id };
        infoWindow.setContent(setTooltipHtml(buildClusterTooltipHtml(cluster, tRef.current)));
        infoWindow.open(mapRef.current, marker);
        setSelectedSiteId(cluster.sites[0].id);
    };

    const openClusterInfo = (cluster, marker) => {
        if (!cluster) {
            return;
        }
        if (clusterHasSpread(cluster) && (mapRef.current.getZoom() || 0) < SITE_ZOOM) {
            const bounds = new window.google.maps.LatLngBounds();
            cluster.sites.forEach((site) => {
                bounds.extend({ lat: Number(site.latitude), lng: Number(site.longitude) });
            });
            mapRef.current.fitBounds(bounds, 72);
            closeTooltip();
            setSelectedSiteId(null);
            return;
        }

        showPinTooltip(cluster, marker, true);
    };

    const focusSite = (site) => {
        if (!mapRef.current) {
            return;
        }
        pendingFocusIdRef.current = site.id;
        setSelectedSiteId(site.id);
        mapRef.current.panTo({ lat: Number(site.latitude), lng: Number(site.longitude) });
        mapRef.current.setZoom(SITE_ZOOM);
    };

    openSiteInfoRef.current = openSiteInfo;
    openClusterInfoRef.current = openClusterInfo;
    showPinTooltipRef.current = showPinTooltip;
    scheduleHoverCloseRef.current = scheduleHoverClose;
    cancelHoverCloseRef.current = cancelHoverClose;
    refreshOpenTooltipRef.current = refreshOpenTooltip;

    useEffect(() => {
        if (!isOpen) {
            setIsLoading(true);
            setMapReady(false);
            setLoadError("");
            setSearchQuery("");
            setSelectedSiteId(null);
            mapRef.current = null;
            markersRef.current = new Map();
            infoWindowRef.current = null;
            clustersByIdRef.current = new Map();
            pendingFocusIdRef.current = null;
            openTooltipRef.current = null;
            tooltipStickyRef.current = false;
            window.clearTimeout(hoverCloseTimeoutRef.current);
            tooltipContentElRef.current = null;
            shouldFitRef.current = true;
            return undefined;
        }

        let cancelled = false;
        let resizeTimeoutId = 0;

        const initMap = async () => {
            try {
                setIsLoading(true);
                setLoadError("");
                await loadGoogleMaps();
                if (cancelled || !mapContainerRef.current) {
                    return;
                }

                const map = new window.google.maps.Map(mapContainerRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    mapTypeId: window.google.maps.MapTypeId.SATELLITE,
                    mapTypeControl: true,
                    mapTypeControlOptions: {
                        mapTypeIds: [
                            window.google.maps.MapTypeId.SATELLITE,
                            window.google.maps.MapTypeId.ROADMAP,
                        ],
                        position: window.google.maps.ControlPosition.TOP_RIGHT,
                    },
                    streetViewControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy",
                });
                map.addListener("click", () => {
                    closeTooltip();
                });
                mapRef.current = map;
                resizeTimeoutId = window.setTimeout(() => {
                    if (cancelled || !mapRef.current || !window.google?.maps) {
                        return;
                    }
                    const liveMap = mapRef.current;
                    const center = liveMap.getCenter();
                    const zoom = liveMap.getZoom();
                    window.google.maps.event.trigger(liveMap, "resize");
                    if (center) {
                        liveMap.setCenter(center);
                    }
                    if (Number.isFinite(zoom)) {
                        liveMap.setZoom(zoom);
                    }
                }, 50);
                if (!cancelled) {
                    setMapReady(true);
                }
            } catch {
                if (!cancelled) {
                    setLoadError(t("chargeMonitor.failedLoadMaps"));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        const frame = window.requestAnimationFrame(() => {
            initMap();
        });

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frame);
            window.clearTimeout(resizeTimeoutId);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !mapReady || !mapRef.current || !window.google?.maps) {
            return undefined;
        }

        const map = mapRef.current;

        const renderClusters = () => {
            if (!map.getProjection()) {
                return;
            }

            const clusters = clusterOverlappingSites(filteredSitesRef.current, map);
            clustersByIdRef.current = new Map(clusters.map((cluster) => [cluster.id, cluster]));
            const visibleIds = new Set(clusters.map((cluster) => cluster.id));

            markersRef.current.forEach((marker, id) => {
                if (!visibleIds.has(id)) {
                    marker.setMap(null);
                    markersRef.current.delete(id);
                }
            });

            const bounds = new window.google.maps.LatLngBounds();
            clusters.forEach((cluster) => {
                const position = { lat: cluster.latitude, lng: cluster.longitude };
                const icon = toGoogleMarkerIcon(
                    buildPieMarkerIcon({
                        available: cluster.available,
                        occupied: cluster.occupied,
                        unavailable: cluster.unavailable,
                        siteCount: cluster.sites.length,
                    })
                );

                let marker = markersRef.current.get(cluster.id);
                if (!marker) {
                    marker = new window.google.maps.Marker({
                        map,
                        position,
                        icon,
                    });
                    marker.addListener("mouseover", () => {
                        const latest = clustersByIdRef.current.get(cluster.id);
                        const current = markersRef.current.get(cluster.id);
                        cancelHoverCloseRef.current?.();
                        showPinTooltipRef.current?.(latest, current, false);
                    });
                    marker.addListener("mouseout", () => {
                        scheduleHoverCloseRef.current?.();
                    });
                    marker.addListener("click", () => {
                        const latest = clustersByIdRef.current.get(cluster.id);
                        const current = markersRef.current.get(cluster.id);
                        openClusterInfoRef.current?.(latest, current);
                    });
                    marker.gitoIconUrl = icon.url;
                    markersRef.current.set(cluster.id, marker);
                } else {
                    marker.setPosition(position);
                    if (marker.gitoIconUrl !== icon.url) {
                        marker.setIcon(icon);
                        marker.gitoIconUrl = icon.url;
                    }
                    marker.setTitle("");
                    marker.setMap(map);
                }

                bounds.extend(position);
            });

            if (shouldFitRef.current && filteredSitesRef.current.length === 1) {
                map.setCenter(bounds.getCenter());
                map.setZoom(SITE_ZOOM);
                shouldFitRef.current = false;
            } else if (shouldFitRef.current && filteredSitesRef.current.length > 1) {
                map.fitBounds(bounds, 48);
                shouldFitRef.current = false;
            }

            const pendingId = pendingFocusIdRef.current;
            if (pendingId) {
                const cluster = clusters.find((item) => item.sites.some((site) => site.id === pendingId));
                const site = sitesByIdRef.current.get(pendingId);
                const marker = cluster ? markersRef.current.get(cluster.id) : null;
                pendingFocusIdRef.current = null;
                if (site && marker) {
                    openSiteInfoRef.current?.(site, marker, true);
                }
            } else {
                refreshOpenTooltipRef.current?.();
            }
        };

        const idleListener = map.addListener("idle", renderClusters);
        renderClusters();

        return () => {
            window.google.maps.event.removeListener(idleListener);
        };
    }, [isOpen, mapReady, searchQuery, siteLayoutKey]);

    useEffect(() => {
        if (!isOpen || !mapReady || !mapRef.current || !window.google?.maps) {
            return;
        }

        clustersByIdRef.current.forEach((cluster, id) => {
            syncClusterFromLatestSites(cluster, sitesByIdRef.current);
            const marker = markersRef.current.get(id);
            if (!marker) {
                return;
            }
            const icon = toGoogleMarkerIcon(
                buildPieMarkerIcon({
                    available: cluster.available,
                    occupied: cluster.occupied,
                    unavailable: cluster.unavailable,
                    siteCount: cluster.sites.length,
                })
            );
            if (marker.gitoIconUrl !== icon.url) {
                marker.setIcon(icon);
                marker.gitoIconUrl = icon.url;
            }
        });
        refreshOpenTooltipRef.current?.();
    }, [filteredSites, isOpen, mapReady]);

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} size="xl" className="flex min-h-0 flex-col !overflow-y-auto">
            <div className="flex min-h-0 flex-1 flex-col gap-4">
                <div>
                    <div className="flex items-start justify-between gap-4">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <FontAwesomeIcon icon={faLocationDot} className="text-primary-500" />
                            {t("chargeMonitor.chargers")}
                        </h3>
                        <div className="flex flex-wrap justify-end gap-x-4 gap-y-1 pt-1">
                            {PIN_COLUMNS.map((column) => (
                                <span
                                    key={column.key}
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold"
                                    style={{ color: column.color }}
                                >
                                    <span
                                        className="inline-block size-2 rounded-full"
                                        style={{ background: column.color }}
                                    />
                                    {statusTotals[column.key]} {t(`chargeMonitor.mapBucket.${column.key}`)}
                                </span>
                            ))}
                        </div>
                    </div>
                    <p className="text-sm text-mute-500 mt-1">
                        {t("chargeMonitor.mapHelp")}
                    </p>
                </div>

                <div className="flex items-center bg-white border border-mute-200 rounded-xl px-3 py-2 w-full">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="text-mute-400 mr-2" />
                    <input
                        type="text"
                        className="w-full bg-transparent focus:outline-none text-sm text-mute-700"
                        placeholder={t("chargeMonitor.mapSearchPlaceholder")}
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
                    <div className="relative h-80 min-h-0 overflow-hidden rounded-xl border border-mute-200 sm:h-96 lg:h-full">
                        <div ref={mapContainerRef} className="h-full w-full" />
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-mute-25/80">
                                <LoadingSpinner text={t("chargeMonitor.loadingMap")} delayMs={0} compact />
                            </div>
                        )}
                        {loadError && (
                            <div className="absolute inset-0 flex items-center justify-center bg-mute-25 p-6 text-center">
                                <p className="text-sm text-error-500">{loadError}</p>
                            </div>
                        )}
                    </div>

                    <div className="max-h-40 min-h-0 overflow-y-auto rounded-xl border border-mute-200 bg-mute-50 p-3 sm:max-h-48 lg:max-h-none lg:h-full">
                        {filteredSites.length === 0 && filteredUnmappedSites.length === 0 ? (
                            <p className="text-sm text-mute-500 py-4 text-center">{t("chargeMonitor.noMatchingSites")}</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {filteredSites.map((site) => (
                                    <button
                                        key={site.id}
                                        type="button"
                                        onClick={() => focusSite(site)}
                                        className={`rounded-xl border px-3 py-2 text-left transition ${
                                            selectedSiteId === site.id
                                                ? "border-primary-400 bg-white shadow-sm"
                                                : "border-transparent bg-white/70 hover:bg-white"
                                        }`}
                                    >
                                        <div className="text-sm font-bold text-mute-900">
                                            {site.name === "Unknown site" ? t("chargeMonitor.unknownSite") : site.name}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold">
                                            <span style={{ color: BUCKET_COLORS.available }}>{t("chargeMonitor.availableCount", { count: site.available })}</span>
                                            <span style={{ color: BUCKET_COLORS.occupied }}>{t("chargeMonitor.occupiedCount", { count: site.occupied })}</span>
                                            <span style={{ color: BUCKET_COLORS.unavailable }}>{t("chargeMonitor.unavailableCount", { count: site.unavailable })}</span>
                                        </div>
                                    </button>
                                ))}
                                {filteredUnmappedSites.map((site) => (
                                    <div
                                        key={site.id}
                                        className="rounded-xl border border-transparent bg-white/70 px-3 py-2"
                                    >
                                        <div className="text-sm font-bold text-mute-900">
                                            {site.name === "Unknown site" ? t("chargeMonitor.unknownSite") : site.name}
                                        </div>
                                        <div className="mt-1 text-[11px] font-semibold text-mute-500">
                                            {t("chargeMonitor.missingCoordinates")}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ModalBase>
    );
};

export default SiteOperationsMapModal;
